import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, ShieldCheck, Zap, Sparkles, Star, Cpu, Rocket } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ProductBoxArt from '@/components/ProductBoxArt';
import PaymentMethodDialog from '@/components/PaymentMethodDialog';
import PlatformPicker from '@/components/PlatformPicker';
import ProductSections from '@/components/ProductSections';
import PortraitVideoGallery from '@/components/PortraitVideoGallery';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { api, formatApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const REVIEWS = [
  { name: 'Ash M.', stars: 5, quote: 'The recoil control is unreal. Hit Iridescent in two weeks.' },
  { name: 'Jordan T.', stars: 5, quote: 'Instant download after payment, zero drama. This is the real deal.' },
  { name: 'Kai L.', stars: 5, quote: 'Every update is dialled in. Support in Discord is instant.' },
];

const FAQ = [
  { q: 'Does this work on console?', a: 'Yes — every script is engineered for console play via XIM Matrix. Full compatibility list ships with the download.' },
  { q: 'Do I get updates?', a: 'Owners receive updates for the specific title purchased. Lifetime updates on every script are included with Lucky VIP.' },
  { q: 'How is delivery handled?', a: 'Payment unlocks the download instantly inside your account dashboard. No links are ever shared publicly.' },
  { q: 'Can I get a refund?', a: 'No. Scripts are digital and delivered instantly, so all sales are final, as covered in our Terms of Service. If a file won’t download or is broken, reach out in Discord and we’ll fix it.' },
];

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState(null);
  const [validating, setValidating] = useState(false);
  const [platform, setPlatform] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    api.get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => { setProduct(null); setNotFound(true); });
  }, [id, user?.id]);

  const applyDiscount = async () => {
    if (!discountCode.trim()) return;
    setValidating(true);
    try {
      const { data } = await api.post('/payments/validate-discount', { code: discountCode.trim(), kind: 'product', product_id: id });
      setDiscountApplied(data);
      toast.success(`Code applied — ${data.type === 'percent' ? data.value + '% off' : '$' + data.value + ' off'}`);
    } catch (e) {
      setDiscountApplied(null);
      toast.error(formatApiError(e.response?.data?.detail) || 'Invalid code');
    } finally { setValidating(false); }
  };

  const buy = async () => {
    if (!user) { navigate(`/login?next=/product/${id}`); return; }
    if (product?.coming_soon) { toast('Coming soon – not yet available for purchase.'); return; }
    if (product?.platforms?.length && !platform) { toast.error('Choose PS5 or Xbox first'); return; }
    setPayOpen(true);
  };

  if (!product) return (
    <div className="bg-grain min-h-screen">
      <Navbar />
      {notFound ? (
        <div className="pt-40 text-center" data-testid="product-not-found">
          <p className="font-display font-bold text-3xl text-white">Product not found</p>
          <Link to="/" data-testid="product-not-found-back" className="mt-4 inline-block text-brand hover:underline">Back to the store</Link>
        </div>
      ) : (
        <div className="pt-40 text-center text-white/60">Loading product…</div>
      )}
    </div>
  );

  const features = product.features || [];

  return (
    <div className="bg-grain relative min-h-screen text-white overflow-x-hidden">
      <Navbar />

      <section className="relative pt-32 pb-20 px-6" data-testid="product-hero">
        <div
          className="orb"
          style={{ top: '10%', left: '50%', transform: 'translateX(-50%)', width: 500, height: 500,
                   background: `radial-gradient(circle, ${product.accent}55, transparent 70%)` }}
        />
        <div className="mx-auto max-w-7xl relative">
          <Link
            to="/#products"
            data-testid="back-to-products"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="h-4 w-4" /> All products
          </Link>

          <div className="grid lg:grid-cols-5 gap-10 items-start">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-2"
            >
              <ProductBoxArt product={product} size="lg" />
            </motion.div>

            <div className="lg:col-span-3 space-y-6">
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-brand font-mono mb-3">{product.tagline}</p>
                {product.kbm_only && (
                  <span data-testid="kbm-only-badge" className="inline-flex items-center mb-4 px-3 py-1 rounded-md bg-amber-400/10 border border-amber-400/40 text-amber-300 text-[11px] font-mono font-bold uppercase tracking-[0.3em]">
                    KBM Only
                  </span>
                )}
                <h1 data-testid="product-title" className="font-display font-black text-white text-5xl md:text-6xl tracking-tighter leading-[1]">
                  {product.name}
                </h1>
                <p data-testid="product-description" className="mt-5 text-white/70 text-lg leading-relaxed max-w-2xl">
                  {product.long_description || product.description}
                </p>
                {product.credits && (
                  <p data-testid="product-credits" className="mt-3 text-sm text-white/40 font-mono">
                    {product.credits}
                    {product.discord_url && (
                      <> · <a href={product.discord_url} target="_blank" rel="noreferrer" data-testid="product-discord-link" className="text-brand hover:underline">{product.discord_url.replace('https://', '')}</a></>
                    )}
                  </p>
                )}
              </div>

              {features.length > 0 && (
                <div className="pt-2" data-testid="product-features">
                  <p className="text-xs uppercase tracking-[0.35em] text-white/40 font-mono mb-4">What&apos;s inside</p>
                  <ul className="grid sm:grid-cols-2 gap-y-3 gap-x-6">
                    {features.map((f) => (
                      <li key={f} className="flex items-center gap-3 text-white/90 text-[15px]" data-testid={`feature-${f}`}>
                        <span
                          className="inline-flex items-center justify-center h-6 w-6 rounded-full shrink-0"
                          style={{ background: `${product.accent}22`, color: product.accent }}
                        >
                          <Check className="h-3.5 w-3.5" />
                        </span>
                        <span className="font-medium">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex items-baseline gap-3 pt-2">
                <span className="font-display font-black text-white text-5xl tracking-tighter">${product.price_aud}</span>
                <span className="text-white/50">USD · one-time</span>
                {product.coming_soon && (
                  <span className="ml-2 text-[10px] uppercase tracking-widest text-brand px-2 py-1 rounded-full bg-brand/10 border border-brand/30">
                    Coming Soon
                  </span>
                )}
              </div>

              {product.platforms?.length > 0 && (
                <PlatformPicker platforms={product.platforms} value={platform} onChange={setPlatform} />
              )}

              {/* Discount code */}
              <div className="flex items-stretch gap-2 max-w-md" data-testid="discount-row">
                <input
                  data-testid="discount-input"
                  type="text" value={discountCode}
                  onChange={(e) => { setDiscountCode(e.target.value); setDiscountApplied(null); }}
                  placeholder="Discount code (optional)"
                  className="flex-1 px-4 py-2.5 rounded-full bg-white/[0.04] border border-white/10 focus:border-brand outline-none text-white placeholder-white/30 font-mono uppercase text-sm"
                />
                <button
                  data-testid="apply-discount-btn"
                  onClick={applyDiscount} disabled={validating || !discountCode.trim()}
                  className="px-5 py-2.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-sm text-white disabled:opacity-50 transition-colors"
                >{validating ? '…' : 'Apply'}</button>
              </div>
              {discountApplied && (
                <div className="text-sm text-emerald-400 -mt-3" data-testid="discount-applied">
                  ✓ <span className="font-mono">{discountApplied.code}</span> applied — {discountApplied.type === 'percent' ? `${discountApplied.value}% off` : `$${discountApplied.value} USD off`}
                </div>
              )}

              <button
                data-testid="purchase-btn"
                disabled={loading || product.coming_soon}
                onClick={buy}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-brand text-[#0a0a0a] font-semibold text-lg disabled:opacity-50 hover:shadow-[0_0_44px_rgb(var(--brand-rgb)/0.7)] transition-shadow"
              >
                {loading ? 'Redirecting…' : product.coming_soon ? 'Coming Soon' : `Purchase${platform ? ' ' + platform.toUpperCase() : ''} — $${product.price_aud} USD`}
              </button>

              <PaymentMethodDialog
                open={payOpen}
                onOpenChange={setPayOpen}
                amount={product.price_aud}
                kind="product"
                productId={product.id}
                platform={platform}
                discountCode={discountApplied ? discountApplied.code : null}
              />

              <div className="flex items-center gap-6 pt-2 text-xs uppercase tracking-widest text-white/40 font-mono">
                <span className="inline-flex items-center gap-2"><Zap className="h-3 w-3 text-brand" /> Instant Delivery</span>
                <span className="inline-flex items-center gap-2"><ShieldCheck className="h-3 w-3 text-brand" /> Secure Auth Download</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {(product.sections?.length > 0 || product.important_note) && (
        <section className="relative px-6 pb-16">
          <div className="mx-auto max-w-7xl"><ProductSections product={product} /></div>
        </section>
      )}

      {/* Details Tabs — always visible */}
      <section className="relative px-6 pb-20" data-testid="product-details">
        <div className="mx-auto max-w-7xl">
          <Tabs defaultValue="install" className="w-full">
            <TabsList data-testid="product-tabs" className="bg-white/5 border border-white/10 rounded-full p-1 h-auto">
              <TabsTrigger value="install" className="rounded-full px-5 py-2 data-[state=active]:bg-brand data-[state=active]:text-black">Installation</TabsTrigger>
              {!product.hide_preview && (
                <TabsTrigger value="preview" data-testid="tab-preview" className="rounded-full px-5 py-2 data-[state=active]:bg-brand data-[state=active]:text-black">Preview</TabsTrigger>
              )}
              <TabsTrigger value="faq" className="rounded-full px-5 py-2 data-[state=active]:bg-brand data-[state=active]:text-black">FAQ</TabsTrigger>
              <TabsTrigger value="reviews" className="rounded-full px-5 py-2 data-[state=active]:bg-brand data-[state=active]:text-black">Reviews</TabsTrigger>
            </TabsList>

            <TabsContent value="install" className="mt-8">
              <div className="glass rounded-2xl p-8 max-w-3xl">
                <ol className="space-y-4 text-white/80 text-sm leading-relaxed">
                  <li><span className="text-brand font-mono mr-2">01</span> After purchase, log in to your Lucky Scripts dashboard.</li>
                  <li><span className="text-brand font-mono mr-2">02</span> Download the {product.name} package.</li>
                  <li><span className="text-brand font-mono mr-2">03</span> Import the profile into your XIM Matrix via XIM Manager.</li>
                  <li><span className="text-brand font-mono mr-2">04</span> Bind hotkeys as described in the included README.</li>
                  <li><span className="text-brand font-mono mr-2">05</span> Launch the game — you are live.</li>
                </ol>
              </div>
            </TabsContent>

            <TabsContent value="preview" className="mt-8">
              {product.preview_videos?.length > 0 ? (
                <PortraitVideoGallery videos={product.preview_videos} accent={product.accent} />
              ) : product.preview_video_url ? (
                <div
                  className="relative rounded-2xl overflow-hidden border border-white/10 bg-black max-w-4xl aspect-video"
                  data-testid="preview-video-wrapper"
                >
                  <video
                    data-testid="preview-video"
                    src={product.preview_video_url}
                    controls
                    playsInline
                    preload="metadata"
                    className="absolute inset-0 w-full h-full object-contain bg-black"
                  />
                  <div
                    className="pointer-events-none absolute -inset-6 rounded-3xl"
                    style={{ boxShadow: `0 30px 90px -20px ${product.accent}55` }}
                  />
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {[1, 2].map((i) => (
                    <div key={i} className="relative aspect-video rounded-2xl overflow-hidden border border-white/10 bg-black">
                      <div className="absolute inset-0 flex items-center justify-center text-white/40 text-sm">Preview {i}</div>
                      <div className="orb" style={{ inset: 0, background: `radial-gradient(circle at 50% 50%, ${product.accent}55, transparent 70%)` }} />
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="faq" className="mt-8">
              <div className="max-w-3xl">
                <Accordion type="single" collapsible className="w-full">
                  {FAQ.map((f, i) => (
                    <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
                      <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-white hover:text-white">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-white/60 leading-relaxed">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </TabsContent>

            <TabsContent value="reviews" className="mt-8">
              <div className="grid md:grid-cols-3 gap-5">
                {REVIEWS.map((r) => (
                  <div key={r.name} className="glass rounded-2xl p-6">
                    <div className="flex items-center gap-0.5 text-brand">
                      {Array.from({ length: r.stars }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
                    </div>
                    <p className="mt-3 text-white/80 text-sm leading-relaxed">&ldquo;{r.quote}&rdquo;</p>
                    <div className="mt-4 text-xs uppercase tracking-widest text-white/40">{r.name}</div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Footer />
    </div>
  );
}
